import { useQuery } from "@tanstack/react-query";
import ApiHttp from "byzantine/src/ApiHttp";

export interface CandidateTransaction {
  uuid: string;
  datetime: string;
  amount: string;
  merchant: string;
  description: string;
}

export interface ReceiptSuggestion {
  // id and confidence_score are null on a ledger attach: no suggestion row, no matcher score.
  id: number | null;
  // Null when the candidate transaction has since been deleted (SET_NULL on the FK).
  candidate_transaction: CandidateTransaction | null;
  confidence_score: number | null;
}

export interface ReceiptUserRef {
  uuid: string;
  name: string;
}

export interface ReceiptDetail {
  uuid: string;
  status: string;
  merchant: string | null;
  transaction_date: string | null;
  amount: string | null;
  image_url: string;
  image_mime_type: string | null;
  thumbnail_url: string | null;
  large_thumbnail_url: string | null;
  download_url: string;
  original_filename: string;
  suggestion: ReceiptSuggestion | null;
  uploaded_by: ReceiptUserRef | null;
  uploaded_at: string | null;
  approved_by: ReceiptUserRef | null;
  approved_at: string | null;
  rejected_by: ReceiptUserRef | null;
  rejected_at: string | null;
  // Not yet provided by ReceiptDetailSerializer — tracked as a backend
  // follow-up under FINT-275. Optional until the field ships; NO_MATCH
  // countdown gracefully hides when missing or null.
  retry_expires_at?: string | null;
}

export const RECEIPT_DETAIL_QUERY_KEY = "receiptDetail";

export const useReceipt = (uuid: string | null) => {
  return useQuery<ReceiptDetail>({
    queryKey: [RECEIPT_DETAIL_QUERY_KEY, uuid],
    queryFn: () =>
      ApiHttp.fetch<ReceiptDetail>(`receipts/${uuid}/`, { method: "GET" }),
    enabled: uuid !== null,
    // S3 presigned URLs expire after 5 minutes — keep cached data short-lived
    // so reopening a recently-viewed receipt re-fetches a fresh URL.
    staleTime: 60_000,
    gcTime: 60_000,
    retry: false,
  });
};
